import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { userService } from '../services/api';
import './ChangePasswordForm.css';

type Props = {
  userId: number;
  variant?: 'card' | 'modal';
  title?: string;
  hint?: string;
  submitPassword?: (password: string) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const ChangePasswordForm: React.FC<Props> = ({
  userId,
  variant = 'card',
  title = 'Change password',
  hint,
  submitPassword,
  onSuccess,
  onCancel,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pwd = newPassword.trim();
    const confirm = confirmPassword.trim();
    if (pwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (pwd !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      if (submitPassword) {
        await submitPassword(pwd);
      } else {
        await userService.changePassword(userId, pwd);
      }
      toast.success('Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');
      onSuccess?.();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const formClass = variant === 'modal' ? 'cpw-form cpw-form--modal' : 'cpw-form cpw-form--card';
  const defaultHint =
    variant === 'card'
      ? 'Update your sign-in password. Use at least 6 characters.'
      : 'Enter and confirm your new password (min 6 characters).';
  const hintText = hint ?? defaultHint;

  return (
    <form className={formClass} onSubmit={(e) => void handleSubmit(e)}>
      {variant === 'card' ? (
        <>
          <h3>{title}</h3>
          <p className="cpw-hint">{hintText}</p>
        </>
      ) : (
        <p className="cpw-hint">{hintText}</p>
      )}
      <label className="cpw-label">
        <span>New password</span>
        <input
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Min 6 characters"
          disabled={saving}
          required
          minLength={6}
        />
      </label>
      <label className="cpw-label">
        <span>Confirm password</span>
        <input
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Re-enter password"
          disabled={saving}
          required
          minLength={6}
        />
      </label>
      <div className="cpw-actions">
        {onCancel ? (
          <button type="button" className="cpw-btn cpw-btn--ghost" disabled={saving} onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="cpw-btn cpw-btn--primary" disabled={saving}>
          {saving ? 'Saving…' : 'Update password'}
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
